<template>
  <div class="editor">
    <div class="rounded-lg email-editor-menu-bar">
      <div class="menubar px-4 space-x-3 text-sm pt-4 pb-4 flex justify-between text-gray-400 rounded-t-lg bg-brand-light-blue">
        <button class="" :class="{ 'is-active': editor.isActive('bold') }" @click="editor.commands.toggleBold">
          <font-awesome-icon :icon="['fas', 'bold']" />
        </button>

        <button class="" :class="{ 'is-active': editor.isActive('italic') }" @click="editor.commands.toggleItalic">
          <font-awesome-icon :icon="['fas', 'italic']" />
        </button>

        <!-- <button class="" :class="{ 'is-active': editor.isActive('underline') }" @click="editor.commands.toggleUnderline">
        <font-awesome-icon :icon="['fas', 'underline']" />
      </button> -->

        <!-- <button class="" :class="{ 'is-active': editor.isActive('strike') }" @click="editor.commands.toggleStrike">
        <font-awesome-icon :icon="['fas', 'strikethrough']" />
      </button> -->

        <!-- <button class="" :class="{ 'is-active': editor.isActive('paragraph') }" @click="editor.commands.setParagraph">
        <font-awesome-icon :icon="['fas', 'paragraph']" />
      </button> -->

        <button class="" :class="{ 'is-active': editor.isActive('bulletList') }" @click="editor.commands.toggleBulletList">
          <font-awesome-icon :icon="['fas', 'list-ul']" />
        </button>

        <button class="" :class="{ 'is-active': editor.isActive('orderedList') }" @click="editor.commands.toggleOrderedList">
          <font-awesome-icon :icon="['fas', 'list-ol']" />
        </button>

        <button v-if="editor.isActive('link')" :class="{ 'is-active': editor.isActive('link') }" @click="editor.chain().focus().unsetLink().run()">
          <font-awesome-icon :icon="['fas', 'unlink']" />
        </button>

        <button v-if="!editor.isActive('link')" :class="{ 'is-active': editor.isActive('link') }" @click="showLinkPrompt">
          <font-awesome-icon :icon="['fas', 'link']" />
        </button>

        <button class="" @click="showImagePrompt">
          <font-awesome-icon :icon="['fas', 'image']" />
        </button>

        <button class="" @click="showHtmlPrompt">
          <font-awesome-icon :icon="['fas', 'code']" />
        </button>

        <button class="" @click="showVideoPrompt">
          <font-awesome-icon :icon="['fas', 'play-circle']" />
        </button>

        <button class="" @click="editor.commands.undo">
          <font-awesome-icon :icon="['fas', 'undo-alt']" />
        </button>

        <button class="" @click="editor.commands.redo">
          <font-awesome-icon :icon="['fas', 'redo-alt']" />
        </button>

        <!-- push last toolbar item to the right -->
        <div class="flex-grow"></div>

        <button class="font-bold" @click="insertCta">
          Insert CTA
        </button>

        <!-- merge fields -->
        <merge-field-dropdown :options="emailMergeFieldDropdownOptions" :command="insertMergeField" />
      </div>
    </div>
    <!-- /menubar -->

    <div class="el-textarea el-input--4">
      <div class="el-textarea__inner border-none">
        <editor-content class="mt-1 text-brand-black font-medium text-sm leading-6 h-60 overflow-y-scroll" :editor="editor" />
        <span class="el-input__count" v-if="maxlength > 0">{{ length }}/{{ maxlength }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'; // required for this.$prompt
import { mapGetters } from 'vuex';
import { Editor, EditorContent } from '@tiptap/vue-2';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Youtube from '@tiptap/extension-youtube';
import MergeField from '@/components/UI/Editor/MergeField/MergeFieldExtension';
import MergeFieldDropdown from '@/components/UI/Editor/MergeField/MergeFieldDropdown.vue';
import CTA from '@/components/UI/Editor/CTA/CTAExtension';
import { imagePrompt, videoPrompt, htmlPrompt, linkPrompt } from '@/components/UI/Editor/utils';
import { InsertHtml } from '@/components/UI/Editor/InsertHtml';

export default {
  name: 'Editor',
  props: {
    placeholder: String,
    maxlength: {
      type: Number,
      default: 0,
    },
  },
  components: {
    EditorContent,
    MergeFieldDropdown,
  },
  data() {
    return {
      editor: new Editor({
        disableInputRules: true,
        extensions: [
          StarterKit,
          InsertHtml,
          Image,
          MergeField,
          CTA,
          Link.configure({
            openOnClick: false,
            defaultTarget: '_blank',
            HTMLAttributes: {
              class: 'text-brand-blue underline font-semibold',
            },
          }),
          Youtube.configure({
            width: '100%',
            modestBranding: true, // hide youtube logo from control bar
          }),
        ],
        content: this.placeholder,
        editorProps: {
          handleTextInput: (view) => {
            if (this.maxlength && view.state.doc.textContent.length >= this.maxlength && view.state.selection.empty) {
              return true;
            }

            return false;
          },
          handlePaste: (view, event, slice) => {
            if (this.maxlength && view.state.doc.textContent.length + slice.size > this.maxlength) {
              slice.content = new DocumentFragment();
            }
          },
        },
      }),
      length: 0,
    };
  },
  computed: {
    ...mapGetters(['mergeFields']),
    emailMergeFieldDropdownOptions() {
      return [
        {
          label: 'Email Merge Fields',
          items: this.mergeFields,
        },
      ];
    },
  },
  created() {
    // Set the length based on the textContent from ProseMirror
    this.length = this.editor.state.doc.textContent.length;

    // Emit generated HTML back up to the parent component
    this.editor.on('update', ({ editor }) => {
      this.relayContents();
      this.length = editor.getText().length;
    });

    // Emit on load
    this.relayContents(false);
  },
  methods: {
    relayContents(includeUpdate = true) {
      if (includeUpdate) {
        this.$emit('update', this.editor.getHTML());
      }
      this.$emit('jsonUpdate', this.editor.getJSON());
    },
    focusEditor() {
      this.$nextTick(() => {
        this.editor.chain().focus().run();
      });
    },
    showImagePrompt() {
      imagePrompt(this.$prompt, this.editor, this.focusEditor);
    },
    showHtmlPrompt() {
      htmlPrompt(this.$prompt, this.editor, this.focusEditor);
    },
    showVideoPrompt() {
      videoPrompt(this.$prompt, this.editor, this.focusEditor);
    },
    showLinkPrompt() {
      linkPrompt(this.$prompt, this.editor, this.focusEditor);
    },
    insertMergeField({ id, label, fallback }) {
      this.editor.commands.insertMergeField({ id, label, fallback });
    },
    insertCta() {
      this.editor.commands.insertCta();
    },
  },

  beforeDestroy() {
    this.editor.destroy();
  },
};
</script>

<style lang="scss">
.email-editor-menu-bar .is-active {
  color: black;
}
.email-editor-menu-bar button {
  color: #3c5ccf;
  border: none;
  background: none;
  cursor: pointer;
  outline: none;
}

/* Overrides for ProseMirror styles because TipTap doesn't give us a way to add classes inside the editor */
.editor .ProseMirror {
  outline: none !important;
  min-height: 4em;

  ul {
    list-style: disc;
    padding-left: 1.1em;
  }

  ol {
    list-style: decimal;
    padding-left: 1.1em;
  }
}

.editor .el-input__count {
  bottom: -22px;
  @apply text-gray-500;
}
</style>
